import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { tierDef, type TierId } from '@snakefeast/shared';
import { AVATAR_EMOJI } from '../lib/avatars';
import { Icon } from './Icon';
import { GameIcon, Glyph } from './GameIcon';

export function Modal({ title, onClose, children, sheet = true }: { title?: string; onClose: () => void; children: ReactNode; sheet?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  // Callers pass inline onClose arrows; keyed on them, this effect re-ran on every parent render and
  // focus() yanked the caret out of whatever input the user was typing in (e.g. auth over the live preview).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', onKey);
    // Keep focus a child already took via autoFocus (React applies it before this effect runs).
    if (!ref.current?.contains(document.activeElement)) ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className={`overlay${sheet ? ' sheet' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        {title && (
          <div className="row" style={{ marginBottom: 12 }}>
            <h2 className="grow">{title}</h2>
            <button className="btn ghost small icon" onClick={onClose} aria-label="close">
              <Icon name="x" size={18} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function Confirm({ text, onConfirm, onCancel, danger }: { text: string; onConfirm: () => void; onCancel: () => void; danger?: boolean }) {
  const { t } = useTranslation();
  return (
    <Modal onClose={onCancel}>
      <p style={{ marginTop: 0 }}>{text}</p>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={onCancel}>{t('common.cancel')}</button>
        <button className={`btn ${danger ? 'danger' : 'primary'}`} onClick={onConfirm}>{t('common.confirm')}</button>
      </div>
    </Modal>
  );
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button className={`switch${on ? ' on' : ''}`} role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} />;
}

/** `grid` lays many or long options out in equal columns instead of one row that overflows. */
export function Segmented<T extends string>({ value, options, onChange, grid }: { value: T; options: Array<{ value: T; label: ReactNode }>; onChange: (v: T) => void; grid?: boolean }) {
  return (
    <div className={`segmented${grid ? ' grid' : ''}`} role="radiogroup">
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Preset avatar if picked, else the Google photo, else the first letter. */
export function Avatar({ avatar, avatarUrl, name, size = 40, premium }: { avatar: string | null; avatarUrl?: string | null; name: string; size?: number; premium?: boolean }) {
  const emoji = avatar ? AVATAR_EMOJI[avatar] : null;
  const [broken, setBroken] = useState(false);
  const photo = !emoji && avatarUrl && !broken ? avatarUrl : null;
  return (
    <div className={`avatar${premium ? ' premium' : ''}`} style={{ width: size, height: size, fontSize: size * 0.48 }} aria-hidden="true">
      {emoji ??
        (photo ? (
          <img src={photo} alt="" width={size} height={size} referrerPolicy="no-referrer" loading="lazy" onError={() => setBroken(true)} />
        ) : (
          <span style={{ fontWeight: 800, fontSize: size * 0.4 }}>{name.slice(0, 1).toUpperCase()}</span>
        ))}
    </div>
  );
}

const ROMAN = { 1: 'I', 2: 'II', 3: 'III' } as const;

/** Ranked tier badge: icon + name (+ division). */
export function TierBadge({ tier, division, compact }: { tier: TierId; division?: 1 | 2 | 3 | null; compact?: boolean }) {
  const { t } = useTranslation();
  const def = tierDef(tier);
  const label = `${t(`tiers.${tier}`)}${division ? ` ${ROMAN[division]}` : ''}`;
  return (
    <span className={`tier-badge tier-${tier}`} style={{ ['--tier' as string]: def.color }} title={label}>
      <Glyph e={def.icon} />
      {!compact && <span>{label}</span>}
    </span>
  );
}

/** `count`: the number rolls up (results, rewards). */
export function Coins({ value, delta, count }: { value: number; delta?: boolean; count?: boolean }) {
  return (
    <span className="coins" title="coins">
      <GameIcon name="coin" size="1.15em" />
      {delta && value > 0 ? '+' : ''}
      {count ? <CountUp to={value} /> : value.toLocaleString()}
    </span>
  );
}

export function Spinner() {
  return <div className="spin" role="status" aria-label="loading" />;
}

export function Flag({ country }: { country: string | null }) {
  if (!country || country.length !== 2) return null;
  const flag = String.fromCodePoint(...[...country.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));
  return <span title={country} aria-label={country}>{flag}</span>;
}

export function PresenceDot({ status }: { status?: string }) {
  return <span className={`dot ${status ?? 'offline'}`} />;
}

/** Whether motion is off: the app's own setting or the system's (Root mirrors both into this class). */
export const motionOff = () => typeof document !== 'undefined' && document.documentElement.classList.contains('reduce-motion');

/** A number that rolls up to `to` (from `from`), easing out; shown at once when motion is off. */
export function CountUp({ to, from = 0, ms = 900 }: { to: number; from?: number; ms?: number }) {
  const [v, setV] = useState(() => (motionOff() ? to : from));
  useEffect(() => {
    if (motionOff()) {
      setV(to);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      setV(Math.round(from + (to - from) * (1 - (1 - k) ** 3)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, from, ms]);
  return <>{v.toLocaleString()}</>;
}
